import { Clock, Package, Layers, Route, AlertTriangle, TrendingUp, Info } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { EmptyState } from '@/components/shared/EmptyState';
import type { Trace } from '@/features/traceability/type/Tool';
import { KeyIndicatorCard, KeyIndicatorCardSkeleton } from './KeyIndicatorCard';
import { formatDuration, formatNumber } from '@/utils/format';

type AnalyticsKeyIndicatorProps = {
  traces: Trace[];
  isLoading: boolean;
};

export function AnalyticsKeyIndicator({ traces, isLoading }: AnalyticsKeyIndicatorProps) {
  const { t } = useTranslation();

  // Calculate aggregated metrics
  const metrics = traces.reduce(
    (acc, trace) => {
      // Total duration
      if (trace.totalMachiningDuration !== null) {
        acc.totalDuration += trace.totalMachiningDuration;
        acc.totalDurationCount++;
      }

      // Duration by piece
      if (trace.pieceMachiningDuration !== null) {
        acc.durationByPieceSum += trace.pieceMachiningDuration;
        acc.durationByPieceCount++;
      }

      // Number of pieces
      if (trace.machinedPieces !== null) {
        acc.totalPieces += trace.machinedPieces;
      }

      // Volume removed
      if (trace.machinedVolume !== null) {
        acc.totalVolume += trace.machinedVolume;
      }

      // Distance traveled
      if (trace.machinedDistance !== null) {
        acc.totalDistance += trace.machinedDistance;
      }

      // Malround
      if (trace.malround !== null) {
        acc.malroundSum += trace.malround;
        acc.malroundCount++;
      }

      return acc;
    },
    {
      totalDuration: 0,
      totalDurationCount: 0,
      durationByPieceSum: 0,
      durationByPieceCount: 0,
      totalPieces: 0,
      totalVolume: 0,
      totalDistance: 0,
      malroundSum: 0,
      malroundCount: 0,
    }
  );

  // Calculate averages
  const avgDurationByPiece =
    metrics.durationByPieceCount > 0
      ? metrics.durationByPieceSum / metrics.durationByPieceCount
      : 0;
  const avgMalround = metrics.malroundCount > 0 ? metrics.malroundSum / metrics.malroundCount : 0;

  // Render loading state, no data state, or metrics
  if (isLoading) {
    return (
      <section className="space-y-4">
        <div>
          <h2 className="section-title">{t('analytics.dashboard.keyIndicators.title')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('analytics.dashboard.keyIndicators.description')}
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <KeyIndicatorCardSkeleton key={i} />
          ))}
        </div>
      </section>
    );
  }

  // No traces available
  if (traces.length === 0) {
    return (
      <section className="space-y-4">
        <div>
          <h2 className="section-title">{t('analytics.dashboard.keyIndicators.title')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('analytics.dashboard.keyIndicators.description')}
          </p>
        </div>
        <EmptyState
          icon={Info}
          title={t('analytics.dashboard.keyIndicators.noData')}
          description={t('analytics.dashboard.keyIndicators.noDataDescription')}
        />
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <div>
        <h2 className="section-title">{t('analytics.dashboard.keyIndicators.title')}</h2>
        <p className="text-sm text-muted-foreground">
          {t('analytics.dashboard.keyIndicators.description')}
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {/* Total Duration */}
        <KeyIndicatorCard
          icon={<Clock className="h-5 w-5" />}
          label={t('analytics.dashboard.keyIndicators.totalDuration')}
          value={formatDuration(metrics.totalDuration)}
          description={t('analytics.dashboard.keyIndicators.totalDurationDesc', {
            count: traces.length,
          })}
          iconColor="text-info"
        />

        {/* Duration by Piece */}
        <KeyIndicatorCard
          icon={<TrendingUp className="h-5 w-5" />}
          label={t('analytics.dashboard.keyIndicators.durationByPiece')}
          value={formatDuration(avgDurationByPiece)}
          description={t('analytics.dashboard.keyIndicators.durationByPieceDesc')}
          iconColor="text-primary"
        />

        {/* Number of Pieces */}
        <KeyIndicatorCard
          icon={<Package className="h-5 w-5" />}
          label={t('analytics.dashboard.keyIndicators.numberOfPieces')}
          value={metrics.totalPieces.toString()}
          description={t('analytics.dashboard.keyIndicators.numberOfPiecesDesc')}
          iconColor="text-success"
        />

        {/* Volume Removed */}
        <KeyIndicatorCard
          icon={<Layers className="h-5 w-5" />}
          label={t('analytics.dashboard.keyIndicators.volumeRemoved')}
          value={`${formatNumber(metrics.totalVolume)} cm³`}
          description={t('analytics.dashboard.keyIndicators.volumeRemovedDesc')}
          iconColor="text-warning"
        />

        {/* Distance Traveled */}
        <KeyIndicatorCard
          icon={<Route className="h-5 w-5" />}
          label={t('analytics.dashboard.keyIndicators.distanceTraveled')}
          value={`${formatNumber(metrics.totalDistance)} mm`}
          description={t('analytics.dashboard.keyIndicators.distanceTraveledDesc')}
          iconColor="text-info"
        />

        {/* Malround (Average) */}
        <KeyIndicatorCard
          icon={<AlertTriangle className="h-5 w-5" />}
          label={t('analytics.dashboard.keyIndicators.malround')}
          value={`${formatNumber(avgMalround, 2)} µm`}
          description={t('analytics.dashboard.keyIndicators.malroundDesc')}
          iconColor="text-destructive"
        />
      </div>
    </section>
  );
}
